import { useQuery } from "@tanstack/react-query";
import { fetchQuizFilterList } from "../services/quizFilterService";

const TOP_FILTER_KEYS = new Set(["filter_all", "free_paid", "filter_time"]);

const QUICK_FILTER_GROUP_CONFIG = {
  quiz_years: {
    title: "Quiz Year",
    icon: "bi bi-calendar3",
    valueKey: "filter_year",
    fallbackValueKeys: ["quiz_year", "year", "value"],
  },
  quiz_live_types: {
    title: "Quiz Type",
    icon: "bi bi-chat-text",
    valueKey: "filter_live",
    fallbackValueKeys: ["value"],
  },
  solved_unsolved: {
    title: "Solved / Unsolved",
    icon: "bi bi-check2-square",
    valueKey: "filter_solved",
    fallbackValueKeys: ["value"],
  },
  languages: {
    title: "Language",
    icon: "bi bi-translate",
    valueKey: "language",
    fallbackValueKeys: ["lang_short_name", "value"],
  },
  months: {
    title: "Month",
    icon: "bi bi-calendar-event",
    valueKey: "filter_month",
    fallbackValueKeys: ["value"],
  },
  exams: {
    title: "Exam",
    icon: "bi bi-journal-text",
    valueKey: "filter_exam",
    fallbackValueKeys: ["value"],
  },
  tags: {
    title: "Tag",
    icon: "bi bi-tags",
    valueKey: "filter_tag",
    fallbackValueKeys: ["value"],
  },
};

function resolveQuizFilterEndpoint(routeKey) {
  const normalizedRouteKey = Number(routeKey);

  if (normalizedRouteKey === 3) {
    return "/quiz/pyp-filter-list";
  }

  if (normalizedRouteKey === 2) {
    return "/quiz/series-filter-list";
  }

  return "/quiz/quiz-filter-list";
}

function resolveOptionValue(option, primaryKey, fallbackKeys = []) {
  const candidateKeys = [primaryKey, ...fallbackKeys, "value", "id"];

  for (const key of candidateKeys) {
    if (option?.[key] !== undefined && option?.[key] !== null && option?.[key] !== "") {
      return option[key];
    }
  }

  return "";
}

function normalizeFlatFilters(filters = []) {
  return (Array.isArray(filters) ? filters : [])
    .filter((filter, index) => {
      const filterKey = `${filter?.filter_key || ""}`.trim();
      return filterKey && TOP_FILTER_KEYS.has(filterKey);
    })
    .map((filter, index) => ({
      id: `${filter?.filter_key || "filter"}-${filter?.value ?? index}`,
      title: filter?.name || "Filter",
      filterKey: filter?.filter_key || "",
      value: filter?.value,
    }));
}

function normalizeQuickFilterGroups(payload = {}) {
  return Object.entries(QUICK_FILTER_GROUP_CONFIG).reduce((groups, [key, config]) => {
    const source = Array.isArray(payload?.[key]) ? payload[key] : [];
    if (!source.length) {
      return groups;
    }

    groups.push({
      id: key,
      title: config.title,
      icon: config.icon,
      filterKey: config.valueKey,
      defaultValue: `All ${config.title}`,
      options: source.map((option, index) => ({
        id: `${key}-${resolveOptionValue(option, config.valueKey, config.fallbackValueKeys) || index}`,
        label: option?.name || option?.title || `Option ${index + 1}`,
        value: resolveOptionValue(option, config.valueKey, config.fallbackValueKeys),
      })),
    });

    return groups;
  }, []);
}

export function useQuizFilterList(params = {}, options = {}) {
  const endpoint = resolveQuizFilterEndpoint(params?.route_key);

  return useQuery({
    queryKey: ["quizFilterList", endpoint, params],
    queryFn: async () => {
      const { payload } = await fetchQuizFilterList(params, endpoint);

      return {
        topFilters: normalizeFlatFilters(payload?.filters),
        quickFilterGroups: normalizeQuickFilterGroups(payload),
      };
    },
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    placeholderData: (previousData) =>
      previousData ?? {
        topFilters: [],
        quickFilterGroups: [],
      },
  });
}
